import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
// Card #1992 AC7 - a ruling's `at` is a genuine server instant
// (new Date().toISOString(), not an FVD Access stamp), so it renders through
// common-fe's formatVenueTime (the show's real venueTimezone). The
// classDisplay.js local formatVenueTime is NOT that: it is reserved for
// FVD-sourced scheduledTime/rideTime (#1811). scribe-fe has no
// show.venueTimezone plumbed into ScoringForm yet, so calls omit `timeZone`
// and fall back to the Pacific default, the same fallback every other
// scribe-fe time display already assumes.
import { formatVenueTime } from "common-fe";
import {
  canMarkRideRuling,
  resolveRulingAlert,
  rulingMenuItemState,
} from "../util/rideRuling";

// Card #3075 - the ride-level No Show (#1992) and Excused (#2064) gavel UI,
// pulled out of ScoringForm.jsx so its banner, menu section and confirm
// dialog can be rendered and tested on their own. Behavior, copy and
// data-testids are unchanged from the inline versions.

const KINDS = {
  noShow: {
    bannerTestId: "no-show-alert-banner",
    headerTestId: "no-show-alert-header",
    undoTestId: "no-show-undo-button",
    alertHeaderKey: "scoring.noShowAlertHeader",
    alertBodyKey: "scoring.noShowAlertBody",
    undoKey: "scoring.noShowUndoButton",
    undoErrorKey: "scoring.gavelRideNoShowUndoError",
    menuTestId: "gavel-menu-ride-no-show",
    menuKey: "scoring.gavelRideNoShowMenuItem",
    alreadyKey: "scoring.gavelRideNoShowAlreadyMarked",
    confirmTitleKey: "scoring.gavelRideNoShowConfirmTitle",
    confirmBodyKey: "scoring.gavelRideNoShowConfirmBody",
    cancelKey: "scoring.gavelRideNoShowCancel",
    confirmKey: "scoring.gavelRideNoShowConfirm",
    errorKey: "scoring.gavelRideNoShowError",
    confirmTestId: "gavel-ride-no-show-confirm-button",
  },
  excused: {
    bannerTestId: "excused-alert-banner",
    headerTestId: "excused-alert-header",
    undoTestId: "excused-undo-button",
    alertHeaderKey: "scoring.excusedAlertHeader",
    alertBodyKey: "scoring.excusedAlertBody",
    undoKey: "scoring.excusedUndoButton",
    undoErrorKey: "scoring.gavelRideExcusedUndoError",
    menuTestId: "gavel-menu-ride-excused",
    menuKey: "scoring.gavelRideExcusedMenuItem",
    alreadyKey: "scoring.gavelRideExcusedAlreadyMarked",
    confirmTitleKey: "scoring.gavelRideExcusedConfirmTitle",
    confirmBodyKey: "scoring.gavelRideExcusedConfirmBody",
    cancelKey: "scoring.gavelRideExcusedCancel",
    confirmKey: "scoring.gavelRideExcusedConfirm",
    errorKey: "scoring.gavelRideExcusedError",
    confirmTestId: "gavel-ride-excused-confirm-button",
  },
};

// Renders on EVERY panel position's sheet for this ride while the record is
// active (delivered by useRideNoShow/useRideExcused's poll). "Who" is the
// resolved judge name, never the raw sub (see resolveRulingAlert); Undo is
// only rendered for the position that could have caused it (C).
function RideRulingAlertBanner({ kind, record, klass, judgePosition, onUndo, undoing, undoError }) {
  const { t } = useTranslation("score");
  const cfg = KINDS[kind];
  const alert = resolveRulingAlert(record, klass, judgePosition);
  if (!alert) return null;
  const byName =
    alert.who.kind === "name"
      ? alert.who.name
      : alert.who.kind === "position"
        ? t("scoring.noShowByPositionFallback", { position: alert.who.position })
        : t("scoring.noShowByUnknown");
  const whenLabel = formatVenueTime(alert.at);
  return (
    <Box
      data-testid={cfg.bannerTestId}
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "error.main",
        borderRadius: 2,
      }}
    >
      <Typography
        data-testid={cfg.headerTestId}
        variant="overline"
        sx={{
          display: "block",
          mb: 0.5,
          fontWeight: 700,
          lineHeight: 1.2,
          color: "error.main",
        }}
      >
        {t(cfg.alertHeaderKey)}
      </Typography>
      <Typography variant="body2" sx={{ mb: alert.canUndo ? 1 : 0 }}>
        {t(cfg.alertBodyKey, { who: byName, when: whenLabel })}
      </Typography>
      {undoError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t(cfg.undoErrorKey)}
        </Alert>
      )}
      {alert.canUndo && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          loading={undoing}
          onClick={onUndo}
          data-testid={cfg.undoTestId}
        >
          {t(cfg.undoKey)}
        </Button>
      )}
    </Box>
  );
}

export function NoShowAlertBanner({ noShow, ...rest }) {
  return <RideRulingAlertBanner kind="noShow" record={noShow} {...rest} />;
}

export function ExcusedAlertBanner({ excused, ...rest }) {
  return <RideRulingAlertBanner kind="excused" record={excused} {...rest} />;
}

/**
 * The gavel menu's "Ride-level (all judges)" section (#1992 AC1/AC2, #2064):
 * rendered only at judge position C. Must be a child of a MUI Menu/MenuList.
 */
export function RideRulingMenuItems({
  judgePosition,
  noShow,
  excused,
  onOpenNoShow,
  onOpenExcused,
}) {
  const { t } = useTranslation("score");
  if (!canMarkRideRuling(judgePosition)) return null;
  const noShowState = rulingMenuItemState(noShow);
  const excusedState = rulingMenuItemState(excused);
  return (
    <>
      <Divider />
      <Typography
        variant="overline"
        sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
      >
        {t("scoring.gavelNoShowSectionHeader")}
      </Typography>
      <MenuItem
        data-testid={KINDS.noShow.menuTestId}
        disabled={noShowState.disabled}
        onClick={onOpenNoShow}
      >
        {noShowState.alreadyMarked
          ? t(KINDS.noShow.alreadyKey)
          : t(KINDS.noShow.menuKey)}
      </MenuItem>
      <MenuItem
        data-testid={KINDS.excused.menuTestId}
        disabled={excusedState.disabled}
        onClick={onOpenExcused}
      >
        {excusedState.alreadyMarked
          ? t(KINDS.excused.alreadyKey)
          : t(KINDS.excused.menuKey)}
      </MenuItem>
    </>
  );
}

/**
 * Confirm dialog for a ride-level ruling. Cancel/onClose are deliberately
 * never gated on the in-flight write (`marking` only puts the confirm button
 * into its loading state): a scribe on a live show floor must always be able
 * to back out.
 */
export function RideRulingConfirmDialog({ kind, open, onClose, onConfirm, marking, markError }) {
  const { t } = useTranslation("score");
  const cfg = KINDS[kind];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t(cfg.confirmTitleKey)}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t(cfg.confirmBodyKey)}</DialogContentText>
        {markError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t(cfg.errorKey)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          {t(cfg.cancelKey)}
        </Button>
        <Button
          variant="contained"
          color="error"
          loading={marking}
          onClick={onConfirm}
          data-testid={cfg.confirmTestId}
        >
          {t(cfg.confirmKey)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
